import React from "react";
import { Pencil, Trash2, Loader } from "lucide-react";
import SearchableSelect from "./SearchableSelect";

export default function DataSupplierTable({
  data,
  loading,
  error,
  currentPage,
  itemsPerPage,
  totalEntries,
  startEntry,
  endEntry,
  maxPages,
  onPageChange,
  onItemsPerPageChange,
  onEdit,
  onDelete,
}) {
  return (
    <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm mt-6 overflow-hidden">
      {loading && (
        <div className="px-6 py-12 flex items-center justify-center text-slate-500">
          <Loader className="w-5 h-5 animate-spin mr-2" />
          Loading data...
        </div>
      )}

      {error && !loading && (
        <div className="px-6 py-4 bg-rose-50 border-b border-rose-200 text-rose-700">
          Error: {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-32">
                    Aksi
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-24">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Nama Supplier
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Alamat Supplier
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    No. Handphone
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Total Produk
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-12 text-center text-slate-400 text-sm">
                      Tidak ada data
                    </td>
                  </tr>
                ) : (
                  data.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-slate-50/70 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => onEdit(item)}
                            className="w-8 h-8 bg-slate-700 text-white rounded-lg hover:bg-slate-800 flex items-center justify-center transition-colors">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => onDelete(item)}
                            className="w-8 h-8 bg-rose-500 text-white rounded-lg hover:bg-rose-600 flex items-center justify-center transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${
                            item.status === "Aktif"
                              ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                              : "bg-rose-50 text-rose-700 ring-1 ring-rose-200"
                          }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                        {item.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {item.address}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {item.phone}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {item.totalProducts}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/40 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-500">
                Showing{" "}
                <span className="font-medium text-slate-700">{startEntry}</span>{" "}
                to{" "}
                <span className="font-medium text-slate-700">{endEntry}</span>{" "}
                of{" "}
                <span className="font-medium text-slate-700">
                  {totalEntries}
                </span>{" "}
                entries
              </span>
              <SearchableSelect
                options={[
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                ]}
                value={itemsPerPage}
                onChange={(value) => onItemsPerPageChange(value)}
                placeholder="Items per page"
                displayKey="label"
                valueKey="value"
                className="w-20"
                searchable={false}
                size="sm"
              />
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ‹‹
              </button>
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ‹
              </button>

              {Array.from({ length: Math.min(3, maxPages) }, (_, i) => {
                let p = currentPage;
                if (currentPage === 1) p = 1;
                else if (currentPage === maxPages) p = maxPages - 2;
                else p = currentPage - 1;

                if (maxPages < 3) p = 1;

                const pageNum = p + i;
                if (pageNum > maxPages || pageNum < 1) return null;

                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-teal-600 text-white shadow-sm shadow-teal-600/20"
                        : "bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-100"
                    }`}>
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() =>
                  onPageChange(Math.min(maxPages, currentPage + 1))
                }
                disabled={currentPage === maxPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ›
              </button>
              <button
                onClick={() => onPageChange(maxPages)}
                disabled={currentPage === maxPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ››
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
